import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import { makeStyles } from "@saleor/macaw-ui";
import { Box, Button, Checkbox, Text } from "@saleor/macaw-ui-next";
import { useEffect, useMemo, useState } from "react";

import { DashboardCard } from "../components/Card";
import CardSpacer from "../components/CardSpacer";
import { TablePagination } from "../components/TablePagination/TablePagination";

const useStyles = makeStyles(
  theme => ({
    container: {
      margin: theme.spacing(5),
      background: theme.palette.background.default,
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      margin: "24px",
    },
    statsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
      gap: theme.spacing(2),
      marginBottom: theme.spacing(3),
    },
    statCard: {
      background: theme.palette.background.paper,
      borderRadius: theme.spacing(1),
      padding: theme.spacing(2),
      boxShadow: theme.shadows[1],
      textAlign: "center",
    },
    activeChip: {
      background: theme.palette.success.light,
      color: theme.palette.success.contrastText,
      fontWeight: 500,
      padding: theme.spacing(0.5, 1.5),
      borderRadius: theme.spacing(2),
      display: "inline-block",
      fontSize: "1.75rem",
    },
    inactiveChip: {
      background: theme.palette.error.light,
      color: theme.palette.error.contrastText,
      fontWeight: 500,
      padding: theme.spacing(0.5, 1.5),
      borderRadius: theme.spacing(2),
      display: "inline-block",
      fontSize: "1.75rem",
    },
    editButton: {
      minWidth: "40px",
      marginRight: theme.spacing(1),
      color: theme.palette.primary.main,
      "&:hover": {
        background: theme.palette.action.hover,
      },
    },
    deleteButton: {
      minWidth: "40px",
      color: theme.palette.error.main,
      "&:hover": {
        background: theme.palette.error.light,
        color: theme.palette.error.contrastText,
      },
    },
    addButton: {
      marginLeft: theme.spacing(2),
      fontSize: "2rem",
    },
    dialogTitle: {
      padding: theme.spacing(2, 3),
    },
    dialogContent: {
      padding: theme.spacing(3),
    },
    dialogActions: {
      padding: theme.spacing(2),
    },
    formField: {
      marginBottom: theme.spacing(2),
    },
    nameCell: {
      fontWeight: 500,
    },
    emailCaption: {
      color: theme.palette.text.hint,
      fontSize: "1.75rem",
    },
    idCell: {
      fontWeight: 500,
      color: theme.palette.text.primary,
    },
    tableHeader: {
      "& .MuiTableCell-root": {
        color: theme.palette.text.secondary,
        fontWeight: 600,
        fontSize: "1.875rem",
        lineHeight: 1.6,
      },
    },
    actionButtons: {
      display: "flex",
      gap: theme.spacing(1),
    },
    toolbar: {
      padding: theme.spacing(3, 3, 0, 3),
    },
    toolbarRow: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(2),
    },
    searchWrapper: {
      display: "flex",
      alignItems: "center",
      borderRadius: theme.spacing(1),
      padding: theme.spacing(1, 2),
      flex: 1,
      maxWidth: 420,
      transition: "background-color 0.2s ease",
      backgroundColor: theme.palette.background.paper,
      border: `none ${theme.palette.divider}`,
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
      },
    },
    searchIcon: {
      color: theme.palette.text.secondary,
      marginRight: theme.spacing(1),
    },
    searchInput: {
      border: "none",
      outline: "none",
      width: "100%",
      fontSize: "14px",
      backgroundColor: "transparent",
      "&:hover": {
        backgroundColor: "transparent",
      },
    },
  }),
  { name: "Wholesaler" },
);

type Vendor = {
  id: number;
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  isactive: boolean;
  created_at: string | null;
};

// Helper function to get today's date in YYYY-MM-DD format
const getTodaysDate = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const Wholesaler = () => {
  const styles = useStyles();
  const API_BASE = `${import.meta.env.VITE_FAST_API_URL}/api/wholesaler`;
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [formValues, setFormValues] = useState({
    id: 0,
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    isactive: true,
  });
  const [page, setPage] = useState(0);
  const [rowNumber, setRowNumber] = useState(50);

  const fetchVendors = () => {
    setLoading(true);
    fetch(`${API_BASE}/`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP error ${res.status}`);

        return res.json();
      })
      .then(data => {
        let vendorArray: Vendor[] = [];

        if (Array.isArray(data)) {
          vendorArray = data;
        } else if (data && Array.isArray(data.vendors)) {
          vendorArray = data.vendors;
        } else if (data && Array.isArray(data.data)) {
          vendorArray = data.data;
        } else {
          console.warn("Unexpected API response format – using empty array", data);
        }

        setVendors(vendorArray);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  const filteredVendors = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    if (!query) {
      return vendors;
    }

    return vendors.filter(vendor => {
      const firstName = vendor.firstname?.toLowerCase() || "";
      const lastName = vendor.lastname?.toLowerCase() || "";
      const fullName = `${firstName} ${lastName}`.toLowerCase();
      const email = vendor.email?.toLowerCase() || "";
      const phone = vendor.phone?.toLowerCase() || "";
      const id = String(vendor.id).toLowerCase();

      return (
        firstName.includes(query) ||
        lastName.includes(query) ||
        fullName.includes(query) ||
        email.includes(query) ||
        phone.includes(query) ||
        id.includes(query)
      );
    });
  }, [vendors, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredVendors.length / rowNumber));
  const currentPage = Math.min(page, totalPages - 1);
  const pagedVendors = useMemo((): Vendor[] => {
    const start = currentPage * rowNumber;
    const end = start + rowNumber;

    return filteredVendors.slice(start, end);
  }, [filteredVendors, currentPage, rowNumber]);

  const openCreate = () => {
    setFormMode("create");
    setFormValues({
      id: 0,
      firstname: "",
      lastname: "",
      email: "",
      phone: "",
      isactive: true,
    });
    setFormOpen(true);
  };

  const openEdit = (vendor: Vendor) => {
    setFormMode("edit");
    setFormValues({
      id: vendor.id,
      firstname: vendor.firstname || "",
      lastname: vendor.lastname || "",
      email: vendor.email || "",
      phone: vendor.phone || "",
      isactive: Boolean(vendor.isactive),
    });
    setFormOpen(true);
  };

  const closeForm = () => setFormOpen(false);

  const handleSubmit = async () => {
    const payload = {
      firstname: formValues.firstname.trim(),
      lastname: formValues.lastname.trim(),
      email: formValues.email.trim(),
      phone: formValues.phone.trim(),
      isactive: formValues.isactive,
      // Add today's date ONLY for create operations
      created_at: formMode === "create" ? getTodaysDate() : undefined,
    };

    const url = formMode === "create" ? `${API_BASE}/` : `${API_BASE}/${formValues.id}`;
    const method = formMode === "create" ? "POST" : "PUT";

    const res = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      alert(`Save failed (HTTP ${res.status})`);

      return;
    }

    closeForm();
    fetchVendors();
  };

  const handleDelete = async (vendor: Vendor) => {
    const ok = window.confirm(`Delete vendor ${vendor.firstname} ${vendor.lastname}?`);

    if (!ok) return;

    const res = await fetch(`${API_BASE}/${vendor.id}`, { method: "DELETE" });

    if (!res.ok) {
      alert(`Delete failed (HTTP ${res.status})`);

      return;
    }

    fetchVendors();
  };

  const handlePageChange = (direction: "next" | "prev"): void => {
    setPage(prev => {
      if (direction === "next") {
        return Math.min(prev + 1, totalPages - 1);
      }

      return Math.max(prev - 1, 0);
    });
  };

  const handleRowNumberChange = (value: number): void => {
    setRowNumber(value);
    setPage(0);
  };

  const hasVendors = filteredVendors.length > 0;
  const showEmptyState = !loading && !hasVendors;

  return (
    <Box className={styles.container}>
      {/* Header */}
      <Box className={styles.header}>
        <Box>
          <Text as="h1" size={8} fontWeight="bold">
            Vendor List
          </Text>
          <Text size={3} color="default2">
            Manage your vendor directory and quick actions in one place.
          </Text>
        </Box>

        <Button variant="primary" onClick={openCreate}>
          Add vendors
        </Button>
      </Box>

      {/* Table */}
      <DashboardCard>
        <Box className={styles.toolbar}>
          <Box className={styles.toolbarRow}>
            <Box className={styles.searchWrapper}>
              <SearchIcon className={styles.searchIcon} />
              <input
                className={styles.searchInput}
                value={searchQuery}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setSearchQuery(event.target.value);
                  setPage(0);
                }}
                placeholder="Search vendors..."
              />
            </Box>
          </Box>
        </Box>
        <Table>
          <TableHead className={styles.tableHeader}>
            <TableRow>
              <TableCell>S.No</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Text>Loading...</Text>
                </TableCell>
              </TableRow>
            )}

            {showEmptyState && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Text>No vendors found</Text>
                </TableCell>
              </TableRow>
            )}

            {hasVendors &&
              pagedVendors.map((v, index) => (
                <TableRow key={v.id} hover>
                  <TableCell className={styles.idCell}>
                    {currentPage * rowNumber + index + 1}
                  </TableCell>

                  <TableCell>
                    <Text className={styles.nameCell} fontWeight="medium">
                      {v.firstname?.trim()} {v.lastname?.trim()}
                      <span style={{ display: "block", fontSize: "12px", color: "#777" }}>
                        (ID: {v.id})
                      </span>
                    </Text>
                  </TableCell>

                  <TableCell>
                    <Text>{v.email?.trim()}</Text>
                  </TableCell>

                  <TableCell>
                    <Text>{v.phone?.trim()}</Text>
                  </TableCell>

                  <TableCell>
                    {v.isactive ? (
                      <span className={styles.activeChip}>Active</span>
                    ) : (
                      <span className={styles.inactiveChip}>Inactive</span>
                    )}
                  </TableCell>

                  <TableCell>
                    <Text>{v.created_at ? new Date(v.created_at).toLocaleDateString() : "-"}</Text>
                  </TableCell>

                  <TableCell>
                    <Box className={styles.actionButtons}>
                      <Button
                        variant="tertiary"
                        size="small"
                        onClick={() => openEdit(v)}
                        className={styles.editButton}
                      >
                        Edit
                      </Button>

                      <Button
                        variant="tertiary"
                        size="small"
                        onClick={() => handleDelete(v)}
                        className={styles.deleteButton}
                      >
                        Delete
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>

        {hasVendors && (
          <Box paddingTop={4}>
            <TablePagination
              rowNumber={rowNumber}
              onRowNumberChange={handleRowNumberChange}
              hasNextPage={currentPage < totalPages - 1}
              hasPreviousPage={currentPage > 0}
              onNextPage={() => handlePageChange("next")}
              onPreviousPage={() => handlePageChange("prev")}
            />
          </Box>
        )}
      </DashboardCard>

      <CardSpacer />

      {/* Dialog */}
      <Dialog open={formOpen} onClose={closeForm} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>
          <Text size={4} fontWeight="bold">
            {formMode === "create" ? "Add Wholesaler" : "Edit Wholesaler"}
          </Text>
        </DialogTitle>

        <DialogContent className={styles.dialogContent}>
          <Box className={styles.formField}>
            <input
              className={styles.searchInput}
              value={formValues.firstname}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFormValues({ ...formValues, firstname: e.target.value })
              }
              placeholder="First Name"
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            />
          </Box>
          <Box className={styles.formField}>
            <input
              className={styles.searchInput}
              value={formValues.lastname}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFormValues({ ...formValues, lastname: e.target.value })
              }
              placeholder="Last Name"
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            />
          </Box>
          <Box className={styles.formField}>
            <input
              className={styles.searchInput}
              type="email"
              value={formValues.email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFormValues({ ...formValues, email: e.target.value })
              }
              placeholder="Email"
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            />
          </Box>
          <Box className={styles.formField}>
            <input
              className={styles.searchInput}
              value={formValues.phone}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFormValues({ ...formValues, phone: e.target.value })
              }
              placeholder="Phone"
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            />
          </Box>
          <Box>
            <Checkbox
              checked={formValues.isactive}
              onCheckedChange={(checked: boolean) =>
                setFormValues({
                  ...formValues,
                  isactive: checked,
                })
              }
            >
              <Text>Active</Text>
            </Checkbox>
          </Box>
        </DialogContent>

        <DialogActions className={styles.dialogActions}>
          <Button onClick={closeForm} variant="secondary">
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="primary">
            {formMode === "create" ? "Create" : "Update"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Wholesaler;
